import { DynamicFurniturePanel } from "./DynamicFurniturePanel";
import {
  Box,
  BoxSelect,
  Circle,
  Image as ImageIcon,
  Minus,
  Spline,
  Square,
  Text as TextIcon
} from "lucide-react";
import React from "react";
import { Shape } from "../../../../../types";
import { PanelShell } from "../../../../ui/PanelShell";
import { useMapCanvasShapes } from "../../../contexts/MapCanvasContext";
import { useMapEditorSelection } from "../../../contexts/MapEditorContext";
import { EditorPanelComponentProps } from "../../EditorLayout/EditorLayoutContext";
import { PanelCollapseButton } from "../../EditorLayout/PanelCollapseButton";
import { CompositeTemplatePanelControls } from "../SpotPanel/CompositeTemplatePanelControls";
import { SpotPanelControls } from "../SpotPanel/SpotPanelControls";
import { WallPanelControls } from "../WallPanel/WallPanelControls";
import { WallPathPanelControls } from "../WallPathPanel/WallPathPanelControls";
import { ZonePanelControls } from "../ZonePanel/ZonePanelControls";
import { CommonBorderFillPanelControls } from "./common/CommonBorderFillPanelControls";
import { CommonColorPanelControls } from "./common/CommonColorPanelControls";
import { CommonPositionPanelControls } from "./common/CommonPositionPanelControls";
import { CommonSizePanelControls } from "./common/CommonSizePanelControls";
import { CommonTextPanelControls } from "./common/CommonTextPanelControls";
import { IconCategoryPanelControls } from "./common/IconCategoryPanelControls";
import { IconLinkPanelControls } from "./common/IconLinkPanelControls";
import { RectPanelControls } from "./RectPanelControls";

type ShapePanelProps = EditorPanelComponentProps;

export interface ShapeSectionOptions {
  shapes: Shape[];
}

interface ShapeSectionProperties {
  title: string;
  icon: React.FC;
  panels: React.FC<ShapeSectionOptions>[];
}

const getShapeSection = (shapes: Shape[]): ShapeSectionProperties | null => {
  const shape = shapes[0];
  if (!shape) return null;
  const isSameType = shapes.every((_shape) => _shape.type === shape.type);
  const type = isSameType ? shape.type : "mixed";
  switch (type) {
    case "mixed": {
      const hasSpot = shapes.some((s) => s.type === "spot");
      if (hasSpot) {
        return {
          title: "Composite",
          icon: BoxSelect,
          panels: [CompositeTemplatePanelControls]
        };
      }
      return null;
    }
    case "spot":
      return {
        title: "Resource",
        icon: Circle,
        panels: [
          CommonColorPanelControls,
          SpotPanelControls,
          CommonTextPanelControls,
          CompositeTemplatePanelControls
        ]
      };
    case "rectangle":
      return {
        title: "Rectangle",
        icon: Square,
        panels: [
          RectPanelControls,
          CommonSizePanelControls,
          CommonColorPanelControls,
          CommonBorderFillPanelControls,
          CommonPositionPanelControls
        ]
      };
    case "circle":
      return {
        title: "Circle",
        icon: Circle,
        panels: [
          CommonSizePanelControls,
          CommonColorPanelControls,
          CommonBorderFillPanelControls,
          CommonPositionPanelControls
        ]
      };
    case "dynamic-furniture":
      return {title: "Dynamic furniture",icon:Box,panels:[DynamicFurniturePanel,CommonColorPanelControls,CommonPositionPanelControls]};
    case "furniture":
      return {
        title: "Furniture",
        icon: Box,
        panels: [
          CommonColorPanelControls,
          CommonSizePanelControls,
          CommonTextPanelControls
        ]
      };
    case "icon":
      return {
        title: "Icon",
        icon: ImageIcon,
        panels: [
          CommonColorPanelControls,
          CommonSizePanelControls,
          CommonTextPanelControls,
          IconCategoryPanelControls,
          IconLinkPanelControls
        ]
      };
    case "text":
      return {
        title: "Text",
        icon: TextIcon,
        panels: [CommonTextPanelControls, CommonPositionPanelControls]
      };
    case "zone":
      return {
        title: "Zone",
        icon: BoxSelect,
        // CommonColorPanelControls is intentionally omitted: a zone's
        // fill/border color is now driven by its category type, so the
        // generic color picker would be misleading.
        panels: [ZonePanelControls, CommonPositionPanelControls]
      };
    case "wall":
      return {
        title: "Wall",
        icon: Minus,
        panels: [WallPanelControls, CommonColorPanelControls]
      };
    case "wall-path":
      return {
        title: "Wall Path",
        icon: Spline,
        panels: [WallPathPanelControls, CommonColorPanelControls]
      };
  }
  return {
    title: "Shape",
    icon: Square,
    panels: [CommonPositionPanelControls]
  };
};

export const ShapePanel: React.FC<ShapePanelProps> = ({ side }) => {
  const { selectedIds } = useMapEditorSelection();
  const { shapes } = useMapCanvasShapes();

  const { selectedShapes, selectedSection } = React.useMemo(() => {
    const selected = shapes.filter((shape) => selectedIds.includes(shape.id));
    return {
      selectedShapes: selected,
      selectedSection: getShapeSection(selected)
    };
  }, [shapes, selectedIds]);

  return (
    <PanelShell
      title={selectedSection?.title || "Inspector"}
      icon={selectedSection?.icon || Box}
      actions={<PanelCollapseButton side={side} />}
    >
      {!selectedSection && (
        <div className="px-4 py-8 text-center text-neutral-400 text-xs">
          Select an object on the map to edit its properties
        </div>
      )}
      <div className={"pb-6"}>
        {selectedSection?.panels.map((Panel, index) => (
          // Arrow-function components have no displayName (and minified
          // function names can collide in production), so the position in
          // the static per-section panel list is the stable identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: static list, never reordered
          <Panel key={index} shapes={selectedShapes} />
        ))}
      </div>
    </PanelShell>
  );
};
